<template>
  <form class="leave-review-form" @submit.prevent="onSubmit">
    <div class="leave-review-form__field">
      <label class="leave-review-form__label" for="leave-review-name">
        Как вас представить <span class="leave-review-form__req" aria-hidden="true">*</span>
      </label>
      <input
        id="leave-review-name"
        v-model="name"
        type="text"
        class="leave-review-form__input"
        placeholder="Например: Екатерина К."
        required
        :class="{ 'is-error': submitted && !name.trim() }"
      />
    </div>

    <div class="leave-review-form__field">
      <label class="leave-review-form__label" for="leave-review-email">
        Электронная почта <span class="leave-review-form__req" aria-hidden="true">*</span>
      </label>
      <input
        id="leave-review-email"
        v-model="email"
        type="email"
        class="leave-review-form__input"
        placeholder="name@example.com"
        required
        :class="{ 'is-error': submitted && !email.trim() }"
      />
    </div>

    <div class="leave-review-form__field">
      <label class="leave-review-form__label" for="leave-review-phone">
        Телефон <span class="leave-review-form__req" aria-hidden="true">*</span>
      </label>
      <input
        id="leave-review-phone"
        v-model="phone"
        type="tel"
        class="leave-review-form__input"
        placeholder="+7 (___) ___-__-__"
        required
        :class="{ 'is-error': submitted && !phone.trim() }"
      />
    </div>

    <div class="leave-review-form__targets">
      <ReviewSearchSelect
        label="Отделение"
        placeholder="Найти отделение…"
        :options="departmentOptions"
        :value-id="departmentId"
        :value-name="departmentName"
        @input="onDepartment"
      />
      <ReviewSearchSelect
        label="Врач"
        placeholder="Найти врача…"
        :options="employeeOptions"
        :value-id="employeeId"
        :value-name="employeeName"
        @input="onEmployee"
      />
      <div class="leave-review-form__field">
        <label class="leave-review-form__label" for="leave-review-theme">Тема (прочее)</label>
        <select id="leave-review-theme" v-model="theme" class="leave-review-form__input">
          <option value="">Не выбрано</option>
          <option v-for="category in allCategories" :key="category" :value="category">
            {{ category }}
          </option>
        </select>
      </div>
    </div>
    <p v-if="submitted && !hasTarget" class="leave-review-form__error">
      Укажите отделение, врача или тему
    </p>

    <div class="leave-review-form__field">
      <label class="leave-review-form__label" for="leave-review-text">
        Ваш отзыв <span class="leave-review-form__req" aria-hidden="true">*</span>
      </label>
      <textarea
        id="leave-review-text"
        v-model="review"
        class="leave-review-form__input leave-review-form__textarea"
        rows="6"
        placeholder="Расскажите о вашем опыте"
        required
        :class="{ 'is-error': submitted && !review.trim() }"
      />
    </div>

    <div class="leave-review-form__field">
      <p class="leave-review-form__label">
        Оценка <span class="leave-review-form__req" aria-hidden="true">*</span>
      </p>
      <div class="leave-review-form__stars" role="group" aria-label="Оценка от 1 до 5">
        <button
          v-for="star in 5"
          :key="star"
          type="button"
          class="leave-review-form__star"
          :class="{ 'is-active': star <= (hoverRating || rating) }"
          :aria-label="star + ' из 5'"
          @click="rating = star"
          @mouseenter="hoverRating = star"
          @mouseleave="hoverRating = 0"
        >
          ★
        </button>
      </div>
      <p v-if="submitted && !rating" class="leave-review-form__error">Выберите оценку</p>
    </div>

    <div class="leave-review-form__field leave-review-form__consent">
      <p v-if="submitted && !consent" class="leave-review-form__error">
        *Нет согласия на обработку персональных данных
      </p>
      <label class="leave-review-form__consent-label">
        <input
          v-model="consent"
          type="checkbox"
          class="leave-review-form__consent-input"
          :class="{ 'is-error': submitted && !consent }"
        />
        Я даю свое согласие на обработку персональных данных
      </label>
      <p class="leave-review-form__consent-note color-gray text-circe-14">
        С
        <router-link
          to="/about/privacy-policy"
          class="color-green text-circe-14"
          style="text-decoration: underline;"
        >политикой обработки персональных данных</router-link>
        и
        <router-link
          to="/appeal/consent"
          class="color-green text-circe-14"
          style="text-decoration: underline;"
        >согласием на обработку персональных данных</router-link>
        можно ознакомиться по ссылкам.
      </p>
    </div>

    <p v-if="errorMessage" class="leave-review-form__error">{{ errorMessage }}</p>
    <p v-if="successMessage" class="leave-review-form__success">{{ successMessage }}</p>

    <div class="leave-review-form__actions">
      <button class="index-btn" type="submit" :disabled="loading">
        {{ loading ? 'Отправка…' : 'Отправить отзыв' }}
      </button>
    </div>
  </form>
</template>

<script>
import reviewsApi from '@/api/reviews'
import ReviewSearchSelect from '@/components/Reviews/ReviewSearchSelect.vue'

const CATEGORIES = [
  'О родах',
  'О беременности',
  'О неонатологии',
  'О врачах',
  'Поликлиника',
  'Гинекология',
  'Средний мед. персонал',
  'Условия пребывания и сервис',
]

export default {
  name: 'LeaveReviewForm',
  components: { ReviewSearchSelect },
  data() {
    return {
      name: '',
      email: '',
      phone: '',
      review: '',
      rating: 0,
      hoverRating: 0,
      theme: '',
      allCategories: CATEGORIES,
      departmentId: '',
      departmentName: '',
      employeeId: '',
      employeeName: '',
      departmentOptions: [],
      /** Полный справочник сотрудников (с departmentId) */
      allEmployees: [],
      consent: false,
      submitted: false,
      loading: false,
      errorMessage: '',
      successMessage: '',
    }
  },
  computed: {
    hasTarget() {
      return !!(this.departmentId || this.employeeId || this.theme)
    },
    /** Врач и отделение — независимые поля: всегда весь справочник */
    employeeOptions() {
      return this.allEmployees
    },
  },
  async mounted() {
    await this.loadRefs()
    this.applyQueryPrefill()
  },
  methods: {
    async loadRefs() {
      try {
        const [deps, emps] = await Promise.all([
          reviewsApi.getReviewDepartments(),
          reviewsApi.getReviewEmployees(''),
        ])
        this.departmentOptions = this.unwrapList(deps)
        this.allEmployees = this.unwrapList(emps)
      } catch (e) {
        console.warn('Не удалось загрузить справочники отзывов', e)
      }
    },
    unwrapList(response) {
      const data = response && response.data
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.result)) return data.result
      return []
    },
    applyQueryPrefill() {
      const q = this.$route && this.$route.query ? this.$route.query : {}
      const deptId = String(q.departmentId || '').trim()
      const empId = String(q.employeeId || '').trim()
      if (empId) {
        this.onEmployee({
          id: empId,
          name: (this.allEmployees.find((o) => o.id === empId) || {}).name
            || String(q.employeeName || ''),
        })
      }
      if (deptId && !this.departmentId) {
        const found = this.departmentOptions.find((o) => o.id === deptId)
        this.departmentId = deptId
        this.departmentName = found ? found.name : String(q.departmentName || '')
      }
    },
    onDepartment({ id, name }) {
      this.departmentId = id || ''
      this.departmentName = name || ''
    },
    onEmployee({ id, name }) {
      this.employeeId = id || ''
      this.employeeName = name || ''
      if (!id || this.departmentId) return
      // Подставить отделение врача только если отделение ещё не выбрано
      const emp = this.allEmployees.find((e) => e.id === id)
      const deptId = emp && emp.departmentId
      if (!deptId) return
      const dept = this.departmentOptions.find((d) => d.id === deptId)
      this.departmentId = deptId
      this.departmentName = dept ? dept.name : (emp.departmentName || this.departmentName)
    },
    resetForm() {
      this.name = ''
      this.email = ''
      this.phone = ''
      this.review = ''
      this.rating = 0
      this.theme = ''
      this.departmentId = ''
      this.departmentName = ''
      this.employeeId = ''
      this.employeeName = ''
      this.consent = false
      this.submitted = false
    },
    async onSubmit() {
      this.submitted = true
      this.errorMessage = ''
      this.successMessage = ''

      if (
        !this.name.trim() ||
        !this.email.trim() ||
        !this.phone.trim() ||
        !this.review.trim() ||
        !this.rating ||
        !this.hasTarget ||
        !this.consent
      ) {
        return
      }

      this.loading = true
      try {
        const payload = {
          name: this.name.trim(),
          email: this.email.trim(),
          phone: this.phone.trim(),
          review: this.review.trim(),
          rating: this.rating,
          categories: this.theme ? [this.theme] : [],
          departmentId: this.departmentId || null,
          departmentName: this.departmentName || null,
          employeeId: this.employeeId || null,
          employeeName: this.employeeName || null,
        }
        const response = await reviewsApi.submitReview(payload)
        const body = response && response.data
        const created = body && (body.id || (body.result && body.result.id))
        if ((body && body.status === 'SUCCESS') || created) {
          this.successMessage = 'Спасибо! Отзыв отправлен на модерацию.'
          this.resetForm()
        } else {
          this.errorMessage =
            (body && body.message) || 'Не удалось отправить отзыв'
        }
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
          (e && e.message) ||
          'Не удалось отправить отзыв'
        this.errorMessage = typeof msg === 'string' ? msg : 'Не удалось отправить отзыв'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
